import { FC, useState, useCallback, useMemo } from "react";
import {
  Box,
  Typography,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Checkbox,
  Skeleton,
  Alert,
} from "@mui/material";
import {
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  ContentCopy as DuplicateIcon,
  Delete as DeleteIcon,
  ExpandMore as ExpandMoreIcon,
} from "@mui/icons-material";
import { useRbacHook } from "../../../hooks/useRbacHook";
import {
  camelCaseToTitleCase,
  clonePermissions,
  mergePermissionsWithStructure,
} from "../../../utils/rbacHelpers";
import type { IRbac } from "../../../apis/rbac/rbac.interfaces";
import { logger } from "../../../utils/common";
import { DEFAULT_RBAC_PERMISSIONS } from "./defaultRbacPermissions";

interface RoleMatrixViewProps {
  roleId: string | null;
  instituteId: string;
  onRoleDuplicated?: (newRoleId: string) => void;
  onDeleteRole?: (role: { id: string; name: string; description?: string; instituteId: string }) => void;
}

const RoleMatrixView: FC<RoleMatrixViewProps> = ({
  roleId,
  instituteId,
  onRoleDuplicated,
  onDeleteRole,
}) => {
  const {
    roleWithPermissions,
    isLoadingRole,
    roleError,
    updateRolePermissionsRbac,
    duplicateRole,
    isUpdating,
    isDuplicating,
  } = useRbacHook({ selectedRoleId: roleId });

  const [isEditMode, setIsEditMode] = useState(false);
  const [editedPermissions, setEditedPermissions] = useState<IRbac | null>(
    null
  );

  const mergedPermissions = useMemo(
    () =>
      mergePermissionsWithStructure(
        roleWithPermissions?.permissions,
        DEFAULT_RBAC_PERMISSIONS,
      ),
    [roleWithPermissions?.permissions],
  );

  const permissions = isEditMode && editedPermissions
    ? editedPermissions
    : mergedPermissions;

  const handleEnterEditMode = useCallback(() => {
    setEditedPermissions(clonePermissions(mergedPermissions));
    setIsEditMode(true);
  }, [mergedPermissions]);

  const handleCancelEdit = useCallback(() => {
    setEditedPermissions(null);
    setIsEditMode(false);
  }, []);

  const handleSave = useCallback(async () => {
    if (!roleId || !editedPermissions) return;
    try {
      await updateRolePermissionsRbac({ roleId, permissions: editedPermissions });
      logger.show_success("Permissions updated successfully");
      setEditedPermissions(null);
      setIsEditMode(false);
    } catch {
      logger.show_error("Failed to update permissions");
    }
  }, [roleId, editedPermissions, updateRolePermissionsRbac]);

  const handleDuplicate = useCallback(async () => {
    if (!roleId) return;
    try {
      const res = await duplicateRole(roleId);
      if (res?.data) {
        logger.show_success("Role duplicated successfully");
        onRoleDuplicated?.(res.data.id);
      }
    } catch {
      logger.show_error("Failed to duplicate role");
    }
  }, [roleId, duplicateRole, onRoleDuplicated]);

  const handlePermissionChange = useCallback(
    (module: string, submodule: string, key: "read" | "write" | "delete", value: boolean) => {
      if (!isEditMode || !editedPermissions) return;
      setEditedPermissions((prev) => {
        if (!prev) return prev;
        const next = clonePermissions(prev);
        if (next[module]?.[submodule]) {
          next[module][submodule] = {
            ...next[module][submodule],
            [key]: value,
          };
        }
        return next;
      });
    },
    [isEditMode, editedPermissions]
  );

  const handleBulkAction = useCallback(
    (module: string, key: "read" | "write" | "delete", value: boolean) => {
      if (!isEditMode || !editedPermissions?.[module]) return;
      setEditedPermissions((prev) => {
        if (!prev) return prev;
        const next = clonePermissions(prev);
        const submodules = next[module];
        for (const sub of Object.keys(submodules)) {
          submodules[sub] = { ...submodules[sub], [key]: value };
        }
        return next;
      });
    },
    [isEditMode, editedPermissions]
  );

  if (!roleId) return null;

  if (isLoadingRole) {
    return (
      <Box data-testid="role-matrix-loading">
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Skeleton variant="text" width={180} height={32} />
          <Box display="flex" gap={1}>
            <Skeleton variant="rounded" width={80} height={36} />
            <Skeleton variant="rounded" width={100} height={36} />
          </Box>
        </Box>
        {[1, 2, 3].map((i) => (
          <Box key={i} mb={2} data-testid={`role-matrix-skeleton-${i}`}>
            <Skeleton variant="rounded" height={48} sx={{ mb: 1 }} />
            <Skeleton variant="rounded" height={120} />
          </Box>
        ))}
      </Box>
    );
  }

  if (roleError || !roleWithPermissions) {
    return (
      <Alert severity="error" data-testid="role-matrix-error">
        Failed to load role permissions.
      </Alert>
    );
  }

  const moduleEntries = Object.entries(permissions);

  return (
    <Box data-testid="role-matrix-view">
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        flexWrap="wrap"
        gap={2}
        sx={{ mb: 2 }}
        data-testid="role-matrix-header"
      >
        <Typography variant="h6" fontWeight="medium">
          {roleWithPermissions.name}
        </Typography>
        <Box display="flex" gap={1}>
          {!isEditMode ? (
            <>
              <Button
                variant="outlined"
                size="small"
                startIcon={<EditIcon />}
                onClick={handleEnterEditMode}
                data-testid="role-matrix-edit-btn"
              >
                Edit
              </Button>
              <Button
                variant="outlined"
                size="small"
                startIcon={<DuplicateIcon />}
                onClick={handleDuplicate}
                disabled={isDuplicating}
                data-testid="role-matrix-duplicate-btn"
              >
                {isDuplicating ? "Duplicating..." : "Duplicate"}
              </Button>
              <Button
                variant="outlined"
                size="small"
                color="error"
                startIcon={<DeleteIcon />}
                onClick={() =>
                  roleWithPermissions &&
                  onDeleteRole?.({
                    id: roleWithPermissions.id,
                    name: roleWithPermissions.name,
                    description: roleWithPermissions.description,
                    instituteId: roleWithPermissions.instituteId,
                  })
                }
                data-testid="role-matrix-delete-btn"
              >
                Delete
              </Button>
            </>
          ) : (
            <>
              <Button
                variant="contained"
                size="small"
                startIcon={<SaveIcon />}
                onClick={handleSave}
                disabled={isUpdating}
                data-testid="role-matrix-save-btn"
              >
                {isUpdating ? "Saving..." : "Save"}
              </Button>
              <Button
                variant="outlined"
                size="small"
                startIcon={<CancelIcon />}
                onClick={handleCancelEdit}
                disabled={isUpdating}
                data-testid="role-matrix-cancel-btn"
              >
                Cancel
              </Button>
            </>
          )}
        </Box>
      </Box>

      {moduleEntries.length === 0 ? (
        <Typography
          variant="body2"
          color="text.secondary"
          data-testid="role-matrix-no-permissions"
        >
          No permissions configured.
        </Typography>
      ) : (
        moduleEntries.map(([module, submodules]) => (
          <Accordion
            key={module}
            defaultExpanded
            data-testid={`role-matrix-accordion-${module}`}
            sx={{ mb: 1 }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                width="100%"
                pr={2}
              >
                <Typography variant="subtitle1" fontWeight="medium">
                  {camelCaseToTitleCase(module)}
                </Typography>
                {isEditMode && (
                  <Box display="flex" gap={0.5} onClick={(e) => e.stopPropagation()}>
                    <Button
                      size="small"
                      variant="text"
                      onClick={() => handleBulkAction(module, "read", true)}
                      data-testid={`role-matrix-bulk-${module}-read`}
                    >
                      All Read
                    </Button>
                    <Button
                      size="small"
                      variant="text"
                      onClick={() => handleBulkAction(module, "write", true)}
                      data-testid={`role-matrix-bulk-${module}-write`}
                    >
                      All Write
                    </Button>
                    <Button
                      size="small"
                      variant="text"
                      onClick={() => handleBulkAction(module, "delete", true)}
                      data-testid={`role-matrix-bulk-${module}-delete`}
                    >
                      All Delete
                    </Button>
                  </Box>
                )}
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Submodule</TableCell>
                      <TableCell padding="checkbox">Read</TableCell>
                      <TableCell padding="checkbox">Write</TableCell>
                      <TableCell padding="checkbox">Delete</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {Object.entries(submodules).map(([submodule, perm]) => (
                      <TableRow
                        key={submodule}
                        data-testid={`role-matrix-row-${module}-${submodule}`}
                      >
                        <TableCell>
                          {camelCaseToTitleCase(submodule)}
                        </TableCell>
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={perm.read}
                            disabled={!isEditMode}
                            onChange={(_, v) =>
                              handlePermissionChange(module, submodule, "read", v)
                            }
                            data-testid={`role-matrix-checkbox-${module}-${submodule}-read`}
                          />
                        </TableCell>
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={perm.write}
                            disabled={!isEditMode}
                            onChange={(_, v) =>
                              handlePermissionChange(module, submodule, "write", v)
                            }
                            data-testid={`role-matrix-checkbox-${module}-${submodule}-write`}
                          />
                        </TableCell>
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={perm.delete}
                            disabled={!isEditMode}
                            onChange={(_, v) =>
                              handlePermissionChange(module, submodule, "delete", v)
                            }
                            data-testid={`role-matrix-checkbox-${module}-${submodule}-delete`}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </AccordionDetails>
          </Accordion>
        ))
      )}
    </Box>
  );
};

export default RoleMatrixView;
